import { useState } from "react";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Container from "@/components/base/Container";
import { faqs } from "@/mocks/faq";
import { siteInfo } from "@/mocks/site";

export default function FaqPage() {
  const [openId, setOpenId] = useState<string | null>(faqs[0].id);

  useSeo({
    title: "常见问题 | 咖位观察局的判断标准与更新节奏-咖位观察局",
    description:
      "关于咖位观察局的常见问题解答：判断谁更火的标准、维度分数如何得出、内容更新频率与投稿方式，一次说清我们的方法论与内容边界。",
    keywords: "咖位观察局常见问题,咖位判断标准,维度打分,更新频率,投稿方式",
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          mainEntity: faqs.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer },
          })),
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "/" },
            { "@type": "ListItem", position: 2, name: "常见问题" },
          ],
        },
      ],
    },
  });

  return (
    <>
      <SiteHeader />
      <main className="pt-16 md:pt-20">
        <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "常见问题" }]} />

        <section className="border-b border-background-200/80 bg-background-100 py-12 md:py-16">
          <Container>
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-primary-600">
                <span className="h-px w-6 bg-primary-500" />
                FAQ
              </span>
              <h1 className="mt-4 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
                常见问题
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                关于我们的判断方法、内容原则与更新节奏，先在这里说清楚。如果还有疑问，欢迎通过下方的联系方式告诉我们。
              </p>
            </div>
          </Container>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <Container>
            <div className="mx-auto max-w-3xl divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-100">
              {faqs.map((faq) => {
                const isOpen = openId === faq.id;
                return (
                  <div key={faq.id}>
                    <h2>
                      <button
                        type="button"
                        onClick={() => setOpenId(isOpen ? null : faq.id)}
                        aria-expanded={isOpen}
                        className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left transition-colors hover:bg-background-50 md:px-6"
                      >
                        <span className="font-heading text-sm font-bold text-foreground-900 md:text-base">
                          {faq.question}
                        </span>
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors ${
                            isOpen ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          <i className={isOpen ? "ri-subtract-line text-base" : "ri-add-line text-base"} />
                        </span>
                      </button>
                    </h2>
                    {isOpen ? (
                      <div className="px-5 pb-6 md:px-6">
                        <p className="text-sm leading-relaxed text-foreground-600">{faq.answer}</p>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div className="mx-auto mt-10 max-w-3xl rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-8">
              <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">还有别的问题？</h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                    写信到 {siteInfo.email}，或直接在对比榜单里找找你想看的维度。
                  </p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/compare"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    前往对比榜单
                  </Link>
                  <Link
                    to="/about"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-5 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
                  >
                    了解我们
                  </Link>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}